<template>
  <div class="form-bg">
    <!-- Top Navigation Bar -->
    <div class="top-nav">
      <v-btn 
        class="action-btn" 
        @click="refreshData"
        :disabled="loading"
      >
        <v-icon left>mdi-refresh</v-icon>
        Refresh
      </v-btn>
    </div>

    <div class="form-container">
      <!-- Back Button, outside the card -->
      <div class="back-btn-container">
        <v-btn icon class="back-btn" @click="$router.back()">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
      </div>

      <!-- Screen Header -->
      <div class="screen-header">
        <h1 class="screen-title">View Completed Questions Screen</h1>
        <p class="screen-subtitle">Review completed legal questions and their responses</p>
      </div>

      <!-- Filters and Search -->
      <div class="filters-section">
        <v-row>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="search"
              label="Search by Client Name or Question"
              clearable
              density="comfortable"
              bg-color="#e8e8e8"
              flat
              rounded
              append-inner-icon="mdi-magnify"
              variant="solo"
              @input="debouncedSearch"
            />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-select
              v-model="categoryFilter"
              :items="categoryOptions"
              label="Filter by Category"
              clearable
              density="comfortable"
              bg-color="#e8e8e8"
              flat
              rounded
              variant="solo"
              @update:model-value="filterQuestions"
            />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="dateFilter"
              label="Filter by Date (MM/DD/YYYY)"
              clearable
              density="comfortable"
              bg-color="#e8e8e8"
              flat
              rounded
              variant="solo"
              @input="filterQuestions"
            />
          </v-col>
        </v-row>
      </div>

      <!-- Completed Questions List -->
      <div class="questions-container">
        <div v-if="loading" class="loading-section">
         <v-progress-circular indeterminate color="#002060" size="60"></v-progress-circular>
          <p class="loading-text">Loading completed questions...</p>
        </div>

        <div v-else-if="filteredQuestions.length === 0" class="no-data-section">
          <v-icon size="80" color="#ccc">mdi-clipboard-check-outline</v-icon>
          <h3 class="no-data-title">No Completed Questions Found</h3>
          <p class="no-data-text">There are no completed questions matching your criteria.</p>
        </div>

        <div v-else class="questions-list">
          <div 
            v-for="question in filteredQuestions" 
            :key="question.id" 
            class="question-card"
          >
            <!-- Client Details Section -->
            <div class="client-details-section">
              <h3 class="section-title">Client Details</h3>
              <div class="client-details-grid">
                <div class="detail-item">
                  <span class="detail-label">Name:</span>
                  <span class="detail-value">{{ question.clientName || 'N/A' }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Surname:</span>
                  <span class="detail-value">{{ question.clientSurname || 'N/A' }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Date Received:</span>
                  <span class="detail-value">{{ formatDate(question.dateAdded) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Date Answered:</span>
                  <span class="detail-value">{{ formatDate(question.answeredAt) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Question Reference Number:</span>
                  <span class="detail-value">{{ question.id || 'N/A' }}</span>
                </div>
              </div>
            </div>

            <!-- Question Section -->
            <div class="question-section">
              <h3 class="section-title">Question</h3>
              <div class="question-content">
                <p class="question-text">{{ question.question }}</p>
                <div class="question-meta">
                  <v-chip 
                    :color="getCategoryColor(question.category)" 
                    size="small"
                    class="mr-2"
                  >
                    {{ question.category }}
                  </v-chip>
                  <span class="question-date">Asked on {{ formatDate(question.dateAdded) }}</span>
                </div>
              </div>
            </div>

            <!-- Response Section -->
            <div class="response-section">
              <h3 class="section-title">Response</h3>
              <div class="response-content">
                <div v-if="question.response" class="response-details">
                  <div v-if="question.response.heading" class="response-heading">
                    <strong>Heading:</strong> {{ question.response.heading }}
                  </div>
                  <div v-if="question.response.body" class="response-body">
                    <strong>Response:</strong>
                    <p>{{ question.response.body }}</p>
                  </div>
                  <div v-if="question.response.conclusion" class="response-conclusion">
                    <strong>Conclusion:</strong> {{ question.response.conclusion }}
                  </div>
                  <div class="response-meta">
                    <span class="response-author">Answered by: {{ question.answeredBy || 'Unknown' }}</span>
                    <span class="response-date">on {{ formatDate(question.answeredAt) }}</span>
                  </div>
                </div>
                <div v-else class="no-response">
                  <p class="no-response-text">No response available</p>
                </div>
              </div>
            </div>

            <!-- View More Details Button (Admin/Executive Only) -->
            <div v-if="isAdminOrExecutive" class="action-section">
              <v-btn 
               color="#002060"
                dark
                rounded
                @click="viewMoreDetails(question)"
                class="view-details-btn"
              >
                <v-icon left>mdi-eye</v-icon>
                View More Details
              </v-btn>
            </div>
          </div>
        </div>
      </div>

      <!-- Notification Dialog -->
      <NotificationDialog
        v-model:visible="showNotification"
        :type="notificationType"
        :title="notificationTitle"
        :message="notificationMessage"
        @close="showNotification = false"
      />
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import { db } from '../firebaseConfig';
import { collection, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import { mapGetters } from 'vuex';
import NotificationDialog from '../components/NotificationDialog.vue';

export default {
  name: 'CompletedQuestions',
  components: {
    NotificationDialog
  },
  computed: {
    ...mapGetters(['user', 'isAdmin', 'isManager']),
    
    isAdminOrExecutive() {
      return this.isAdmin || this.isManager;
    },
    
    filteredQuestions() {
      let filtered = this.questions;
      
      // Search filter
      if (this.search) {
        const searchTerm = this.search.toLowerCase();
        filtered = filtered.filter(q => 
          (q.clientName && q.clientName.toLowerCase().includes(searchTerm)) ||
          (q.clientSurname && q.clientSurname.toLowerCase().includes(searchTerm)) ||
          (q.question && q.question.toLowerCase().includes(searchTerm))
        );
      }
      
      // Category filter
      if (this.categoryFilter) {
        filtered = filtered.filter(q => q.category === this.categoryFilter);
      }
      
      // Date filter
      if (this.dateFilter) {
        filtered = filtered.filter(q => {
          const questionDate = new Date(q.dateAdded).toLocaleDateString('en-US');
          return questionDate === this.dateFilter;
        });
      }
      
      return filtered;
    },
    
    categoryOptions() {
      const categories = [...new Set(this.questions.map(q => q.category))];
      return categories.filter(cat => cat).sort();
    }
  },
  
  data() {
    return {
      loading: true,
      questions: [],
      search: '',
      categoryFilter: null,
      dateFilter: '',
      showNotification: false,
      notificationType: 'success',
      notificationTitle: '',
      notificationMessage: '',
      unsubscribeQuestions: null
    };
  },
  
  methods: {
    async loadCompletedQuestions() {
      try {
        this.loading = true;
        
        // Query for completed questions
        const questionsQuery = query(
          collection(db, 'questions'),
          where('status', '==', 'completed'),
          orderBy('answeredAt', 'desc')
        );
        
        this.unsubscribeQuestions = onSnapshot(questionsQuery, (snapshot) => {
          this.questions = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              clientName: data.clientName || data.client?.firstName || 'Unknown',
              clientSurname: data.clientSurname || data.client?.surname || 'Unknown'
            };
          });
          console.log('Loaded completed questions:', this.questions.length);
          this.loading = false;
        }, (error) => {
          console.error('Error loading completed questions:', error);
          this.loading = false;
          this.showError('Failed to load completed questions');
        });
        
      } catch (error) {
        console.error('Error setting up questions listener:', error);
        this.loading = false;
        this.showError('Failed to load completed questions');
      }
    },
    
    debouncedSearch: debounce(function() {
      // Search is handled by computed property
    }, 300),
    
    filterQuestions() {
      // Filtering is handled by computed property
    },
    
    refreshData() {
      this.loadCompletedQuestions();
      this.showSuccess('Data refreshed successfully');
    },
    
    viewMoreDetails(question) {
      // Navigate to detailed view
      this.$router.push({
        name: 'QaDetails',
        params: { id: question.id },
        query: { view: 'completed' }
      });
    },
    
    getCategoryColor(category) {
      const colors = {
        'Family Law': 'pink',
        'Criminal Law': 'red',
        'Civil Law': 'blue',
        'Corporate Law': 'green',
        'Property Law': 'orange',
        'Employment Law': 'purple'
      };
      return colors[category] || 'grey';
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return dateString;
      }
    },
    
    showSuccess(message) {
      this.notificationType = 'success';
      this.notificationTitle = 'Success!';
      this.notificationMessage = message;
      this.showNotification = true;
    },
    
    showError(message) {
      this.notificationType = 'error';
      this.notificationTitle = 'Error!';
      this.notificationMessage = message;
      this.showNotification = true;
    }
  },
  
  async created() {
    await this.loadCompletedQuestions();
  },
  
  beforeUnmount() {
    if (this.unsubscribeQuestions) {
      this.unsubscribeQuestions();
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

* {
  font-family: 'Montserrat', sans-serif;
}

.form-bg {
  min-height: 100vh;
  background: #f2f2f2;
  padding: 0;
}

.top-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  z-index: 100;
  height: 72px;
}

.action-btn {
  background: #002060 !important;
  color: #fff !important;
  font-weight: 600;
  font-size: 0.95rem;
  height: 48px;
  min-width: 140px;
  box-shadow: 0 2px 8px rgba(0, 32, 96, 0.2);
}

.action-btn:hover {
  background: #002060 !important;
  box-shadow: 0 4px 12px rgba(0, 32, 96, 0.3);
}

.form-container {
  width: 100%;
  padding: 96px 24px 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.back-btn-container {
  position: absolute;
  top: 32px;
  left: 24px;
  z-index: 10;
}

.back-btn {
  background: #fff !important;
  color: #002060 !important;
  width: 48px;
  height: 48px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

.back-btn:hover {
  background: #f5f5f5 !important;
}

.screen-header {
  text-align: center;
  margin-bottom: 32px;
  padding: 32px;
  background: linear-gradient(135deg, #002060 0%, #001547 100%);
  border-radius: 18px;
  color: white;
}

.screen-title {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 8px;
}

.screen-subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
  margin: 0;
}

.filters-section {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.questions-container {
  min-height: 400px;
}

.loading-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.loading-text {
  margin-top: 16px;
  font-size: 1.1rem;
  color: #666;
}

.no-data-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.no-data-title {
  font-size: 1.5rem;
  color: #666;
  margin: 16px 0 8px 0;
}

.no-data-text {
  color: #999;
  margin: 0;
}

.questions-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.question-card {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #002060;
}

.section-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #002060;
  margin-bottom: 16px;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 8px;
}

.client-details-section {
  margin-bottom: 24px;
}

.client-details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 1rem;
  color: #333;
  font-weight: 500;
}

.question-section {
  margin-bottom: 24px;
}

.question-content {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
}

.question-text {
  font-size: 1.1rem;
  color: #333;
  line-height: 1.6;
  margin-bottom: 12px;
}

.question-meta {
  display: flex;
  align-items: center;
  gap: 12px;
}

.question-date {
  font-size: 0.9rem;
  color: #666;
}

.response-section {
  margin-bottom: 24px;
}

.response-content {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
}

.response-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.response-heading {
  font-size: 1rem;
  color: #333;
  font-weight: 500;
}

.response-body {
  font-size: 1rem;
  color: #333;
  line-height: 1.6;
}

.response-body p {
  margin: 8px 0 0 0;
}

.response-conclusion {
  font-size: 1rem;
  color: #333;
  font-weight: 500;
}

.response-meta {
  display: flex;
  gap: 12px;
  font-size: 0.9rem;
  color: #666;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
}

.no-response {
  text-align: center;
  padding: 20px;
}

.no-response-text {
  color: #999;
  font-style: italic;
}

.action-section {
  display: flex;
  justify-content: center;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

.view-details-btn {
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

@media (max-width: 768px) {
  .top-nav {
    padding: 12px 16px;
    height: 64px;
  }
  
  .form-container {
    padding: 80px 16px 16px;
  }
  
  .screen-header {
    padding: 24px 16px;
  }
  
  .screen-title {
    font-size: 1.5rem;
  }
  
  .client-details-grid {
    grid-template-columns: 1fr;
  }
  
  .question-card {
    padding: 16px;
  }
  
  .question-meta {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .response-meta {
    flex-direction: column;
    gap: 4px;
  }
}
</style> 